import moment from "moment-jalaali";
import React, { useEffect, useMemo, useState } from "react";
import { formatFromNow, toMoment } from "@common/helpers/i18n";
import { useEvent } from "@common/hooks";
import { RelativeTimeProps } from "@common/types";
import { useIsRtl } from "@app/hooks";

export function RelativeTime({
  date,
  format,
  ...rest
}: RelativeTimeProps & React.TimeHTMLAttributes<HTMLElement>) {
  const isRtl = useIsRtl();
  const resolvedFormat = format ?? (isRtl ? "jYYYY/jMM/jDD" : "YYYY/MM/DD");
  const momentDate = useMemo(() => toMoment(date, "YYYY/MM/DD HH:mm"), [date]);

  const [relativeDate, setRelativeDate] = useState(() =>
    formatFromNow(momentDate, resolvedFormat),
  );

  const updateRelative = useEvent(() =>
    setRelativeDate(formatFromNow(momentDate, resolvedFormat)),
  );

  useEffect(() => {
    updateRelative();
  }, [resolvedFormat, momentDate, updateRelative]);

  const shouldAutoUpdate = Math.abs(momentDate.diff(moment.now(), "days")) < 7;

  useEffect(() => {
    if (shouldAutoUpdate) {
      return periodic.add(updateRelative);
    }
  }, [updateRelative, shouldAutoUpdate]);

  return (
    <time dateTime={momentDate.toISOString()} {...rest}>
      {relativeDate}
    </time>
  );
}

/**
 * Calls all subscribers once in a period.
 */

const period = 60000;

const periodic = {
  // subscribers = new Set()
  subscribers: [] as Array<() => void>,

  timer: null as any,

  schedule() {
    this.timer = setTimeout(() => {
      // Call all subscribers.
      for (const subscriber of this.subscribers) {
        subscriber();
      }

      // Schedule next iteration.
      this.schedule();
    }, period);
  },

  add(subscriber: () => void) {
    // Add subscriber.
    // this.subscribers.add(subscriber)
    if (this.subscribers.indexOf(subscriber) < 0) {
      this.subscribers.push(subscriber);
    }

    // If it's the first subscriber,
    // start this periodical.
    // if (this.subscribers.size === 1) {
    if (this.subscribers.length === 1 && this.timer === null) {
      this.schedule();
    }

    // Return unsubscribe function.
    return () => this.remove(subscriber);
  },

  remove(subscriber: () => void) {
    // Remove subscriber.
    // this.subscribers.delete(subscriber)
    if (this.subscribers.indexOf(subscriber) >= 0) {
      this.subscribers.splice(this.subscribers.indexOf(subscriber), 1);
    }

    // If it was the last subscriber,
    // stop periodical time refresh.
    // if (this.subscribers.size === 0) {
    if (this.subscribers.length === 0) {
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
    }
  },
};
